import { play } from 'cuelume'

import { buildTwapAlgoParams } from '@/features/order/order-form/lib/build-trigger-fire-payload'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { baseCurrencyHint, dedupeTargets } from '@/features/order/order-form/lib/submitters/shared'
import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import { trackOrderItems } from '@/lib/analytics-order'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import type { useStartTwapAlgo, useStartTwapAlgoBatch } from '@/service/algo.hook'
import { Denomination } from '@/types/generated/common/trading'
import type { Instrument } from '@/types/generated/common/types'

export type SubmitTwapDeps = {
  instruments: Instrument[]
  instrumentMap: Map<string, Instrument>
  startTwap: ReturnType<typeof useStartTwapAlgo>['mutateAsync']
  startTwapBatch: ReturnType<typeof useStartTwapAlgoBatch>['mutateAsync']
}

// Submit TWAP order
export async function submitTwapOrder(ctx: SubmitterContext, deps: SubmitTwapDeps) {
  const { t } = ctx
  const { instruments, instrumentMap, startTwap, startTwapBatch } = deps
  if (!ctx.validateSubmissionRows()) return
  const rawValues = ctx.getRawValues()
  const rows = ctx.getSubmissionRows()

  if (rows.length === 0) {
    toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
    return
  }

  ctx.setIsSubmitting(true)

  try {
    // Build TWAP params for each leg via the shared helper — single source
    // of truth with triggered TWAP, so a fired TWAP mirrors a manual one.
    // The helper validates duration vs cadence and throws a user-facing msg.
    const twapOrderParams = rows.map(({ item, rawItem, account }) => {
      const amount = rawItem.amount
      if (!amount || amount <= 0) {
        throw new Error(t('enterValidAmount'))
      }

      const amountUnit = item.amountUnit ?? ''
      const baseCurrency = resolveBaseCurrency(
        instrumentMap,
        item.symbol,
        baseCurrencyHint(item.baseCurrency)
      )
      const isBaseAmount = item.itemType === 'option' || amountUnit === baseCurrency
      const denomination = isBaseAmount
        ? Denomination.DENOMINATION_BASE
        : Denomination.DENOMINATION_QUOTE

      const optionContractSize =
        item.itemType === 'option'
          ? Math.max(
              1,
              Number(instruments.find((i) => i.symbol === item.symbol)?.contractSize) || 1
            )
          : 1
      const effectiveAmount = amount * optionContractSize

      return buildTwapAlgoParams({
        symbol: item.symbol,
        quantity: effectiveAmount.toString(),
        side: item.side,
        amountUnit,
        accountId: account.accountId,
        denomination,
        raw: rawValues,
      })
    })

    // One correlation id per submission: stamped on the outgoing command(s) and reused as
    // the optimistic submission id, so an error notification echoing it resolves this exact
    // pending order (instead of the latest-for-symbol heuristic).
    const twapSubmissionId = randomUUID()

    // Execute TWAP orders (batch if multiple, single if one)
    if (twapOrderParams.length > 1) {
      await startTwapBatch(twapOrderParams.map((p) => ({ ...p, correlationId: twapSubmissionId })))
    } else {
      await startTwap({ ...twapOrderParams[0], correlationId: twapSubmissionId })
    }

    trackOrderItems('twap', twapOrderParams, ctx.getAnalyticsAccounts(), 'order-form')
    const twapTargets = dedupeTargets(twapOrderParams)
    const selection = ctx.getSelection()
    ctx.triggerOptimistic(twapSubmissionId, twapTargets, selection.instruments, selection.options)
    // No success toast — the WS algoLifecycle STARTED notification already
    // shows one (notification-manager.tsx), and firing both looked like a duplicate.
    ctx.resetFormAfterOrder()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('twapSubmissionFailed'), {
      position: 'bottom-right',
    })
    play('error')
    ctx.setIsSubmitting(false)
  }
}
